import { useSnackbar } from "notistack";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import ReactGA from "react-ga4";
import { useLanguage } from "../context/LanguageContext";

const AcessarLoja = () => {
  const { t, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const loginAttemptedRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "tela_de_login_atleta",
    });
  }, []);

  const getParam = (name) => {
    const val1 = searchParams.get(name);
    if (val1) return val1;

    const val2 = new URLSearchParams(window.location.search).get(name);
    if (val2) return val2;

    if (window.location.hash && window.location.hash.includes("?")) {
      const hashQuery = window.location.hash.split("?")[1];
      const val3 = new URLSearchParams(hashQuery).get(name);
      if (val3) return val3;
    }

    return null;
  };

  const executarLogin = async (payload, targetPath = "/ar/shop") => {
    setLoading(true);
    setStatusMessage(payload.chave ? "Autenticando chave exclusiva..." : "Identificando acesso...");
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/auth/acesso`,
        payload
      );

      if (
        response.data.user.perfil === "Atleta" ||
        response.data.user.perfil === "Assessoria"
      ) {
        localStorage.setItem("tokenAcesso", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.removeItem("token");
        if (response.data.user.idioma) {
          setLanguage(response.data.user.idioma);
        }
        enqueueSnackbar(t("auth.welcome", { name: response.data.user.nome }), {
          variant: "success",
        });

        const destination =
          targetPath === "compras" || targetPath === "/ar/compras"
            ? "/ar/compras"
            : targetPath || "/ar/shop";

        navigate(destination, { replace: true });
      } else {
        enqueueSnackbar("Área exclusiva para atletas e assessorias", {
          variant: "warning",
        });
        navigate("/games", { replace: true });
      }
    } catch (error) {
      console.error("Erro no acesso:", error);
      enqueueSnackbar(
        error.response?.data?.msg || "Código ou chave de acesso inválida",
        { variant: "warning" }
      );
    } finally {
      setLoading(false);
      setStatusMessage("");
    }
  };

  useEffect(() => {
    const keyParam = getParam("k") || getParam("chave");
    const codeParam = getParam("c") || getParam("codigo");
    const toParam = getParam("to") || getParam("redirect") || "/ar/shop";

    if (keyParam && !loginAttemptedRef.current) {
      loginAttemptedRef.current = true;
      executarLogin({ chave: keyParam.trim() }, toParam);
    } else if (codeParam && !loginAttemptedRef.current) {
      loginAttemptedRef.current = true;
      const cleanCode = codeParam.trim();
      setPassword(cleanCode);
      executarLogin({ password: cleanCode.toUpperCase() }, toParam);
    }
  }, [searchParams]);

  const handleAcesso = async (e) => {
    e.preventDefault();
    if (!password) return;
    await executarLogin({ password: password.trim().toUpperCase() }, "/ar/shop");
  };

  return (
    <div className="p-6 bg-base-100 flex justify-center items-center mt-20">
      <div className="container max-w-lg shadow-lg rounded-lg p-5 bg-base-100">
        <div className="justify-center flex mt-4 flex-col">
          <h1 className="text-3xl font-semibold text-center">{t("auth.storeAccess")}</h1>

          <div
            role="alert"
            className="alert alert-warning text-xs sm:text-sm max-w-xs mx-auto mt-4 py-2 px-3 shadow-sm flex items-center justify-center text-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="stroke-current shrink-0 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.332.192 3 1.732 3z"
              />
            </svg>
            <span>{t("auth.restrictedAreaAlert")}</span>
          </div>

          {loading && (
            <div className="my-6 p-4 bg-base-200 rounded-lg flex flex-col items-center justify-center gap-3 text-center">
              <span className="loading loading-spinner loading-md text-primary"></span>
              <span className="text-sm font-medium text-base-content/80">
                {statusMessage || t("auth.authenticating")}
              </span>
            </div>
          )}

          <form
            className="w-full mx-auto max-w-xs mt-6"
            onSubmit={handleAcesso}
          >
            <div className="my-2">
              <label htmlFor="mensagem" className="block text-md mb-2">
                {t("auth.storeAccess")}
              </label>

              <input
                id="password"
                type="password"
                value={password}
                placeholder={t("auth.accessCode")}
                onChange={(e) => setPassword(e.target.value)}
                className="border border-base-300 px-4 py-2 w-full rounded-md"
                required
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 hover:bg-green-800 text-white py-2 px-4 rounded-md my-12 flex items-center justify-center gap-2 font-medium disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    <span>{statusMessage || t("common.loading")}</span>
                  </>
                ) : (
                  t("auth.enter")
                )}
              </button>
              <p className="mb-4">
                {t("auth.noCodeWhatsapp")}
                <a
                  className="link flex justify-center align-bottom text-green-950 mt-2"
                  href={`http://wa.me/5511976006882/?text=${encodeURIComponent(t("auth.whatsappMessage"))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="50px"
                    width="50px"
                    viewBox="0 0 448 512"
                  >
                    <path
                      fill="green"
                      d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
                    />
                  </svg>
                </a>
              </p>
              <Link to="/" className="btn mt-5">
                {t("common.back")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcessarLoja;
